import {logger} from '../../../logger';
import {makeAutoObservable} from 'mobx';
import s from '../../s';
import axios from 'axios';
import {getAntiForgeryToken} from '../../../util';
import {DEFAULT_IMAGE_UPLOAD_ERROR, isBlobObjectUrl, NaturalCropRect, normalizeImageUploadError, revokeBlobObjectUrl} from './image-upload.lifecycle';

declare var __fiu: string;

interface IImageResult {
    hash: string;
    w: number;
    h: number;
    debug: number;
    is_error: boolean;
}

export const IMAGE_UPLOAD_FAILED_MSG = DEFAULT_IMAGE_UPLOAD_ERROR;

/** Shopper-facing upload errors must be plain strings — never Axios/error objects. */
export const toShopperSafeUploadMessage = (err: unknown): string => normalizeImageUploadError(err);

export class ImageUploadStore {
    public file: File | undefined;
    public preparedFile: File | undefined;
    public preparedCrop: NaturalCropRect | undefined;
    public preparingCrop: boolean = false;
    public localObjectUrl: string = '';
    public reso_level: number = 0;
    public cropRequestId: number = 0;
    public fileSelectionId: number = 0;
    public replacementInProgress: boolean = false;

    constructor() {
        makeAutoObservable(this);
    }

    get canConfirmCrop(): boolean {
        return !!this.preparedFile && !!this.preparedCrop && !this.preparingCrop;
    }

    /**
     * Cropped bytes plus blob preview retained for another /api/img/event POST.
     * Distinct from s.ui.image_uploaded, which is cleared on server rejection.
     */
    get hasRetryableLocalUpload(): boolean {
        return !!this.file && isBlobObjectUrl(s.ps.override_d1_img);
    }

    beginFileSelection = (): number => {
        this.fileSelectionId += 1;

        if (this.localObjectUrl || isBlobObjectUrl(s.ps.override_d1_img)) {
            this.replacementInProgress = true;
            this.discardLocalPreview();
        }

        this.file = undefined;
        this.invalidateCrops();
        this.reso_level = 0;
        s.ui.image_upload_error = false;
        s.ui.image_uploaded_msg = '';
        s.ui.image_uploading = false;
        return this.fileSelectionId;
    };

    isCurrentFileSelection = (selectionId: number): boolean => selectionId === this.fileSelectionId;

    beginCropPrepare = (): number => {
        this.cropRequestId += 1;
        this.preparingCrop = true;
        this.preparedFile = undefined;
        this.preparedCrop = undefined;
        return this.cropRequestId;
    };

    completeCropPrepare = (file: File | null, requestId: number, naturalCrop?: NaturalCropRect): boolean => {
        if (requestId !== this.cropRequestId) {
            return false;
        }

        this.preparingCrop = false;

        if (!file || !naturalCrop) {
            this.preparedFile = undefined;
            this.preparedCrop = undefined;
            return false;
        }

        this.preparedFile = file;
        this.preparedCrop = naturalCrop;
        return true;
    };

    failCropPrepare = (requestId: number): void => {
        this.completeCropPrepare(null, requestId);
    };

    failCropRead = (requestId?: number): boolean => {
        if (requestId != null && requestId !== this.cropRequestId) {
            return false;
        }

        this.invalidateCrops();
        s.ui.image_uploading = false;
        this.imageUploadError();
        return true;
    };

    discardLocalPreview = (): void => {
        this.revokeLocalPreview();

        if (isBlobObjectUrl(s.ps.override_d1_img)) {
            s.ps.override_d1_img = '';
        }

        s.ui.image_uploaded = false;
        s.ui.doRender = true;
        s.ui.update_pricing = true;
    };

    invalidateCrops = (): void => {
        this.cropRequestId += 1;
        this.preparingCrop = false;
        this.preparedFile = undefined;
        this.preparedCrop = undefined;
    };

    getConfirmedCropFile = (): File | undefined => {
        if (!this.canConfirmCrop) {
            return undefined;
        }

        return this.preparedFile;
    };

    applyConfirmedCrop = (file?: File): boolean => {
        const confirmed = file || this.getConfirmedCropFile();

        if (!confirmed) {
            return false;
        }

        this.file = confirmed;
        this.revokeLocalPreview();

        let url = '';

        try {
            url = URL.createObjectURL(confirmed);
        } catch (err) {
            logger.error(err);
            this.imageUploadError();
            return false;
        }

        this.localObjectUrl = url;
        s.ps.override_d1_img = url;
        s.ui.image_uploaded = true;
        s.ui.image_uploaded_msg = '';
        s.ui.image_upload_error = false;
        s.ui.update_pricing = true;
        s.ui.doRender = true;
        this.cropRequestId += 1;
        this.preparingCrop = false;
        this.preparedFile = confirmed;
        this.replacementInProgress = false;
        return true;
    };

    revokeLocalPreview = (): void => {
        const url = this.localObjectUrl;
        this.localObjectUrl = '';
        revokeBlobObjectUrl(url);
    };

    resetFileInput = (): void => {
        if (typeof document === 'undefined') {
            return;
        }

        const fileInput: HTMLInputElement | null = document.querySelector('#imageUploadInput');

        if (fileInput) {
            fileInput.value = '';
        }
    };

    cancel = (): void => {
        this.invalidateCrops();
        this.resetFileInput();

        if (this.replacementInProgress) {
            this.discardLocalPreview();
            this.file = undefined;
            this.replacementInProgress = false;
        }
    };

    removeUploadedImage = (): void => {
        this.revokeLocalPreview();
        s.ui.image_uploaded = false;
        s.ui.image_uploaded_msg = '';
        s.ps.image_upload_hash3 = '';
        s.ps.override_d1_img = '';
        s.ui.image_uploading = false;
        s.ui.image_upload_error = false;
        s.ui.doRender = true;
        s.ui.update_pricing = true;
        this.reso_level = 0;
        this.file = undefined;
        this.replacementInProgress = false;
        this.invalidateCrops();
        this.resetFileInput();
    };

    dispose = (): void => {
        this.fileSelectionId += 1;
        this.invalidateCrops();
        this.revokeLocalPreview();
        s.ui.image_uploading = false;

        if (isBlobObjectUrl(s.ps.override_d1_img)) {
            s.ps.override_d1_img = '';
        }
    };

    uploadImage = async () => {
        try {
            s.ui.image_uploading = true;
            s.ui.image_uploaded_msg = '';
            s.ui.image_upload_error = false;
            s.ui.processing_title = 'Image Uploading';
            s.ui.processing_text = 'Please stay on this page until your image is done uploading.';

            if (!this.file) {
                this.imageUploadError();
                return;
            }

            const url = '/api/img/event';
            const config = {
                headers: {
                    RequestVerificationToken: getAntiForgeryToken(),
                },
            };

            const response = await axios.post(url, this.getFormData(), config);
            const ir: IImageResult = response.data;
            logger.log(response.data);

            if (!ir.hash || ir.hash?.includes('error') || ir.hash?.length !== 32 || ir.is_error) {
                this.imageUploadError();
            } else {
                s.ui.image_uploaded_msg = '';
                s.ui.image_upload_error = false;
                s.ps.image_upload_hash3 = ir.hash;
                this.revokeLocalPreview();
                s.ps.override_d1_img = `https://chc-upload.s3.amazonaws.com/d/${ir.hash}.png`;
                s.ui.image_uploaded = true;
                s.ui.doRender = true;
                s.ui.update_pricing = true;
            }
        } catch (err) {
            logger.error(err);
            // Transport failure during ATC (or any upload): keep the selected file,
            // blob/preview URL, and uploaded flag so the shopper can retry without
            // re-picking the image. Only mark the attempt as failed.
            s.ui.image_uploaded_msg = toShopperSafeUploadMessage(err);
            s.ui.image_upload_error = true;
        } finally {
            s.ui.image_uploading = false;
        }
    };

    imageUploadError = (err: unknown = DEFAULT_IMAGE_UPLOAD_ERROR) => {
        s.ui.image_uploaded = false;
        s.ui.image_uploaded_msg = toShopperSafeUploadMessage(err);
        s.ui.image_upload_error = true;
        this.reso_level = 0;
        this.cancel();
    };

    getFormData = (_file?: File) => {
        const formData = new FormData();
        const file = _file || this.file;

        if (file) {
            formData.append('file', file);
            formData.append('fileName', file.name);
        }

        formData.append('doc_width', s.ps.docWidth.toString());
        formData.append('doc_height', s.ps.docHeight.toString());
        formData.append('fiu', __fiu);

        return formData;
    };
}

export const imageUploadStore = new ImageUploadStore();
